import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { tokenManager } from "../services/tokenManager";

const API_BASE_URL = process.env.REACT_APP_URL_API;

export const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30_000,
    headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
    },
    withCredentials: true,
});

// ---------- Request interceptor ----------
apiClient.interceptors.request.use((config) => {
    const token = tokenManager.getAccessToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // Disable caching on all requests
    config.headers["Cache-Control"] = "no-cache, no-store, must-revalidate";
    return config;
});

// ---------- Response interceptor (401 → refresh → retry) ----------
let isRefreshing = false;
let failedQueue: {
    resolve: (token: string) => void;
    reject: (err: unknown) => void;
}[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
    failedQueue.forEach((p) => {
        if (token) p.resolve(token);
        else p.reject(error);
    });
    failedQueue = [];
};

apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        const skipRefreshUrls = ["/v1/auth/login", "/v1/auth/register", "/v1/auth/refresh"];
        const shouldSkip = skipRefreshUrls.some((u) => originalRequest.url?.includes(u));

        if (
            error.response?.status !== 401 ||
            originalRequest._retry ||
            shouldSkip
        ) {
            return Promise.reject(error);
        }

        if (isRefreshing) {
            return new Promise<string>((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return apiClient(originalRequest);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
            const refreshTkn = tokenManager.getRefreshToken();
            const { data } = await axios.post(
                `${API_BASE_URL}/v1/auth/refresh`,
                {},
                {
                    headers: { "x-refresh-token": refreshTkn || "" },
                    withCredentials: true,
                }
            );

            const { accessToken, refreshToken } = data.data;
            tokenManager.setTokens(accessToken, refreshToken);
            processQueue(null, accessToken);

            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return apiClient(originalRequest);
        } catch (refreshError) {
            processQueue(refreshError, null);
            tokenManager.clearTokens();
            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }
);
